<script lang="ts" setup>
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { RadioGroupRoot } from 'reka-ui'
import { faCheckCircle } from '@fortawesome/duotone-regular-svg-icons'
import { getFlytProgressLocally } from '~/utils/flyt'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useFileInfo from '~/composables/useFileInfo'
import useDescriptor from '~/composables/useDescriptor'
import { useContentHelper } from '~/composables/useContentHelper'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import LearningPathLink from '~/components/utils/LearningPathLink.vue'
import AuRadio from '~/components/ui/AuRadio.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const props = defineProps<{
  resource: BaseItem
  isAnchor?: boolean
  noImage?: boolean
  chooseTask?: boolean
  selectedId?: number
}>()

const emit = defineEmits<{
  (e: 'choose', locationId: number): void
}>()

const { t } = useI18n()
const { readableMimeType } = useFileInfo()
const { getTitle, isLearningPath, isFile } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()
const { getDescriptor } = useDescriptor()

const title = computed(() => getTitle(props.resource))
const link = computed(() => buildResourceUrl(props.resource))

const label = computed(() => {
  if (isFile(props.resource) && props.resource.file?.mimeType) {
    return readableMimeType(props.resource.file.mimeType)
  }
  return [
    getDescriptor(props.resource),
    ...(props.resource.tiered ? [t('search.checkboxes.tiered')] : []),
  ].join(', ')
})

const attributes = computed(() => isLearningPath(props.resource)
  ? {
      resource: props.resource
    }
  : {
      link: link.value,
      isAnchor: props.isAnchor
    }
)

const flytStyle = computed(() =>
  getFlytProgressLocally(props.resource.locationId)?.completed && {
    '--card-ring': 'var(--theme-5)',
    '--card-bg': 'var(--theme-20)',
    '--card-hover': 'var(--theme-20)',
  }
)

const selectedLocationId = computed({
  get: () => props.selectedId,
  set: id => emit('choose', Number(id))
})
</script>

<template>
  <div :style="flytStyle" class="animate-fade relative flex max-h-64 w-full flex-col gap-2 bg-(--card-bg) px-6 py-4 text-xl font-bold text-(--card-text) outline outline-(--card-border) transition duration-100 ring-inset has-[a:hover]:bg-(--card-hover) has-[a:hover]:ring has-[a:hover]:ring-(--card-border)">
    <aside
      v-if="resource.new"
      class="bg-green-40 pointer-events-none absolute top-0 right-0 z-20 rounded-bl-2xl p-1.5 pl-2 text-sm font-medium tracking-wider text-white uppercase"
      v-text="t('newShort')"
    />
    <div
      :id="`card-${resource.locationId}`"
      class="contents"
    >
      <div class="text-xs font-semibold text-(--card-accent) uppercase">
        <span v-text="label" />
      </div>

      <slot name="task-number" />

      <div class="text-lg font-medium break-words" v-text="title" />
    </div>

    <img
      v-if="resource.thumbnail && !noImage"
      :src="resource.thumbnail"
      alt=""
      class="size-full overflow-hidden object-cover"
    >

    <component
      :is="isLearningPath(resource) ? LearningPathLink : LinkOrAnchor"
      :aria-labelledby="`card-${resource.locationId}`"
      class="absolute inset-0 ring-offset-1 focus-visible:ring"
      v-bind="attributes"
    />

    <div v-if="!chooseTask" class="relative mt-auto flex items-center gap-4">
      <CardMenu
        :resource="resource"
        button-classes="mr-auto z-10 text-(--card-border) hover:not-disabled:bg-(--card-accent)/20"
        toggle-shade="dark"
        toggle-variant="ghost"
      />
      <font-awesome-icon
        v-if="getFlytProgressLocally(resource.locationId)?.completed"
        :icon="faCheckCircle"
      />
    </div>
    <div
      v-else
      class="relative z-10 mt-auto w-full"
    >
      <RadioGroupRoot v-model="selectedLocationId">
        <label
          :class="{ '!bg-orange-10 !border-green-50 !text-green-50': selectedId === resource.locationId }"
          class="hover:bg-orange-10 inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-orange-50 bg-transparent px-3 py-2 text-sm font-medium text-orange-50 transition-all focus-visible:ring"
        >
          <AuRadio
            :id="String(resource.parentLocationId)"
            :value="resource.locationId"
            shade="dark"
          />
          <span class="truncate">{{ selectedId === resource.locationId ? t('assign.taskChosen') : t('assign.chooseTask') }}</span>
        </label>
      </RadioGroupRoot>
    </div>
  </div>
</template>
